import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, fmtINR } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import UniversityImage from "@/components/UniversityImage";
import { reportFor } from "./DecisionReport";
import {
  ArrowLeft, Printer, Download, Share2, Sparkles, Users, Wallet, BarChart3,
  Plane, Star, Info, ExternalLink, ShieldCheck, Trophy, Lock,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// One Decision Report — premium parent-facing comparison of the 3 picks
// (Safe / Target / Reach). Same data source as the standard Final Verdict
// page (/api/recommendations/top-picks + /api/financial-plan). The standard
// Final Verdict layout is untouched; this is a sibling view at /decision-report/one
// opened from a new "One Decision Report" button on the Final Verdict header.
// ─────────────────────────────────────────────────────────────────────────────

const BAND_TOKENS = {
  safe:   { label: "Safe Pick",   badge: "Best for balanced parent choice", chipBg: "bg-emerald-50", chipText: "text-emerald-700", chipBorder: "border-emerald-200", colHeader: "text-emerald-700", colTint: "bg-emerald-50/40" },
  target: { label: "Target Pick", badge: "Strong all-round option",         chipBg: "bg-amber-50",   chipText: "text-amber-700",   chipBorder: "border-amber-200",   colHeader: "text-amber-700",   colTint: "bg-amber-50/40" },
  reach:  { label: "Reach Pick",  badge: "Stretch — worth a shot",           chipBg: "bg-rose-50",    chipText: "text-rose-700",    chipBorder: "border-rose-200",    colHeader: "text-rose-700",    colTint: "bg-rose-50/40" },
};

export default function OneDecisionReport() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [picks, setPicks] = useState(null);
  const [plans, setPlans] = useState({});
  const [error, setError] = useState(null);
  const [shareToast, setShareToast] = useState(null);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/recommendations/top-picks");
        if (cancelled) return;
        setPicks(data);
        const top = [data.safe?.[0], data.target?.[0], data.reach?.[0]].filter(Boolean);
        const planResults = await Promise.all(top.map(u =>
          api.post("/financial-plan", { university_slug: u.slug, duration_years: 3, inflation_pct: 5 })
            .then(res => [u.slug, res.data]).catch(() => [u.slug, null])
        ));
        if (cancelled) return;
        setPlans(Object.fromEntries(planResults.filter(([, v]) => v)));
      } catch (e) {
        setError(e?.message || "Unable to load decision report");
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const reports = useMemo(() => {
    if (!picks) return [];
    return [
      reportFor(picks.safe?.[0], "safe", plans[picks.safe?.[0]?.slug]),
      reportFor(picks.target?.[0], "target", plans[picks.target?.[0]?.slug]),
      reportFor(picks.reach?.[0], "reach", plans[picks.reach?.[0]?.slug]),
    ].filter(Boolean);
  }, [picks, plans]);

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Unimora — One Decision Report", url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareToast("Link copied to clipboard");
        setTimeout(() => setShareToast(null), 2500);
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="min-h-screen bg-white lg:pl-[240px] print-root">
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        {/* Back link — screen only */}
        <div className="no-print mb-6">
          <button onClick={() => nav("/decision-report")} className="inline-flex items-center gap-2 text-body-sm text-[var(--s500)] hover:text-[var(--s800)]" data-testid="one-report-back">
            <ArrowLeft size={14} strokeWidth={2} /> Back to Final Verdict
          </button>
        </div>

        {/* Header */}
        <header className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div className="min-w-0">
            <div className="eyebrow inline-flex items-center gap-1.5 mb-3">
              <Sparkles size={12} className="text-[var(--ind)]" strokeWidth={2.2} />
              <span>Final Verdict — Parent Decision Card</span>
            </div>
            <h1 className="font-semibold text-[var(--s900)] text-h1 md:text-display leading-[1.05]" data-testid="one-report-title">
              Your decision, <em className="italic text-[var(--ind)]">explained.</em>
            </h1>
            <p className="text-body text-[var(--s600)] mt-3 max-w-2xl leading-relaxed">
              One evidence-backed verdict per band — Safe, Target, Reach — drawn directly from your Decision Engine.
            </p>
          </div>
          <div className="no-print flex flex-wrap items-center gap-2">
            {user && (
              <>
                <button onClick={() => window.print()} className="btn-ghost inline-flex items-center gap-2" data-testid="one-report-print">
                  <Printer size={14} strokeWidth={2} /> Print Comparison
                </button>
                <button onClick={() => window.print()} className="btn-ghost inline-flex items-center gap-2" data-testid="one-report-download" title="Use browser print dialog → Save as PDF">
                  <Download size={14} strokeWidth={2} /> Download PDF
                </button>
                <button onClick={onShare} className="btn-primary inline-flex items-center gap-2" data-testid="one-report-share">
                  <Share2 size={14} strokeWidth={2} /> Share with Parent
                </button>
              </>
            )}
          </div>
        </header>

        {error && <div className="text-[var(--s500)]">{error}</div>}
        {!picks && !error && <SkeletonBlock testid="one-report-loading" />}

        {/* Body — blurred + sign-in overlay for guests, matching the
            DecisionReportDetail / Atlas paywall pattern */}
        <div className="relative">
          <div className={!user ? "blur-[6px] select-none pointer-events-none" : ""}>
            {reports.length === 3 && (
              <>
                <ParentTakeaway reports={reports} />
                <VerdictCards reports={reports} onOpen={(slug) => nav(`/decision-report/${slug}`)} />
                <ParentCareSections reports={reports} />
                <DetailedComparison reports={reports} />
                <TradeOffsRow reports={reports} />
                <ParentNote />
              </>
            )}
          </div>

          {!user && (
            <div className="absolute inset-0 flex items-start justify-center pt-24 px-4 no-print">
              <div className="text-center max-w-md bg-white/90 backdrop-blur-md border border-[var(--s200)] rounded-[var(--r20)] shadow-xl p-8">
                <div className="w-12 h-12 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
                  <Lock size={20} strokeWidth={1.8} />
                </div>
                <h3 className="text-h2 font-semibold text-[var(--s900)] leading-tight">
                  Sign in to read the One Decision Report
                </h3>
                <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed">
                  A single, evidence-backed comparison across your Safe, Target, and Reach picks — drawn from your profile.
                </p>
                <button
                  onClick={startGoogle}
                  data-testid="one-report-unlock"
                  className="btn-primary mt-5 inline-flex items-center justify-center gap-2"
                >
                  Get Early Access
                </button>
                <div className="mt-4 text-micro text-[var(--s500)] space-y-1">
                  <div><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</div>
                  <div><span className="text-[var(--success)] font-semibold">✓</span> Free until October 30</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {shareToast && (
          <div className="no-print fixed bottom-6 right-6 bg-[var(--s900)] text-white text-body-sm px-4 py-2.5 rounded-[var(--r12)] shadow-xl z-50">
            {shareToast}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// ─── Parent Takeaway ─────────────────────────────────────────────────────────

function ParentTakeaway({ reports }) {
  const [safe, target, reach] = reports;
  // Best Value = lowest total cost (tuition+living)
  const bestValue = [...reports].sort((a, b) =>
    ((a.uni.tuition_per_year_inr || 0) + (a.uni.living_per_year_inr || 0)) -
    ((b.uni.tuition_per_year_inr || 0) + (b.uni.living_per_year_inr || 0))
  )[0];
  // Best Career Signal = highest employment rate
  const bestCareer = [...reports].sort((a, b) =>
    (b.uni.employment_rate_pct || 0) - (a.uni.employment_rate_pct || 0)
  )[0];
  return (
    <section className="rounded-[var(--r24)] bg-[var(--ind-soft)] border border-indigo-100 p-6 md:p-8 mb-10" data-testid="one-report-takeaway">
      <div className="flex items-center gap-2.5 mb-5">
        <Sparkles size={16} className="text-[var(--ind)]" strokeWidth={2.2} />
        <h2 className="text-h2 font-semibold text-[var(--s900)]">Parent Takeaway</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TakeawayTile band="safe" picker={safe} headline="Safe Pick" body={safe?.band_rationale} />
        <TakeawayTile band="target" picker={target} headline="Target Pick" body={target?.band_rationale} />
        <TakeawayTile band="reach" picker={reach} headline="Reach Pick" body={reach?.band_rationale} />
      </div>
      <ul className="mt-5 space-y-2 text-body-sm text-[var(--s700)]">
        {bestValue && (
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>
              <span className="font-semibold text-[var(--s900)]">{bestValue.name}</span> is your best-value pick at <span className="font-semibold">{fmtINR((bestValue.uni.tuition_per_year_inr || 0) + (bestValue.uni.living_per_year_inr || 0))}</span> total per year.
            </span>
          </li>
        )}
        {bestCareer && bestCareer.uni.employment_rate_pct && (
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--ind)] shrink-0" />
            <span>
              <span className="font-semibold text-[var(--s900)]">{bestCareer.name}</span> reports the highest employment rate at <span className="font-semibold">{bestCareer.uni.employment_rate_pct}%</span>.
            </span>
          </li>
        )}
      </ul>
    </section>
  );
}

function TakeawayTile({ band, picker, headline, body }) {
  const t = BAND_TOKENS[band];
  if (!picker) return <SkeletonCard />;
  return (
    <div className={`rounded-[var(--r16)] bg-white border ${t.chipBorder} p-4`}>
      <div className={`text-micro font-semibold tracking-[0.05em] uppercase ${t.chipText} mb-1.5`}>{headline}</div>
      <div className="text-h3 font-semibold text-[var(--s900)] leading-snug">{picker.name}</div>
      <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed line-clamp-3">{body || "—"}</p>
    </div>
  );
}

// ─── Verdict Cards ───────────────────────────────────────────────────────────

function VerdictCards({ reports, onOpen }) {
  return (
    <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-testid="one-report-verdict-cards">
      {reports.map((r) => <VerdictCard key={r.slug} r={r} onOpen={() => onOpen(r.slug)} />)}
    </section>
  );
}

function VerdictCard({ r, onOpen }) {
  const t = BAND_TOKENS[r.band];
  const totalYr = (r.uni.tuition_per_year_inr || 0) + (r.uni.living_per_year_inr || 0);
  return (
    <div className="card overflow-hidden hover:shadow-lg transition-shadow" data-testid={`one-report-card-${r.slug}`}>
      <UniversityImage slug={r.slug} name={r.name} aspectClass="aspect-[16/9]" rounded="" showGradientOverlay />
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-micro font-semibold tracking-[0.05em] uppercase ${t.chipText} ${t.chipBg} border ${t.chipBorder} px-2 py-0.5 rounded-full`}>{t.label}</span>
          <span className="text-micro text-[var(--s500)]">#{r.rank} in band</span>
        </div>
        <div className="text-h3 font-semibold text-[var(--s900)] leading-snug">{r.name}</div>
        <div className="text-label text-[var(--s500)] mt-1 flex items-center gap-1.5">
          <span>{r.city}{r.city && " · "}{r.country}</span>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 py-3 px-3.5 rounded-[var(--r12)] bg-[var(--ind-soft)]">
          <div>
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">Decision Score</div>
            <div className="text-h2 font-semibold text-[var(--s900)] leading-none mt-0.5">{r.decisionScore}<span className="text-body-sm text-[var(--s500)] font-normal">/100</span></div>
          </div>
          <Stars value={r.decisionScore} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Metric k="AI Match" v={`${r.matchScore}%`} />
          <Metric k="Acceptance" v={r.acceptance != null ? `${r.acceptance}%` : "—"} />
          <Metric k="Tuition / yr" v={r.tuitionLabel} />
          <Metric k="Living / yr" v={r.livingLabel} />
          <Metric k="Total / yr" v={fmtINR(totalYr)} />
          <Metric k="Visa" v={r.visaReadiness} />
        </div>

        <button onClick={onOpen} className="mt-5 text-label font-semibold text-[var(--ind)] hover:underline inline-flex items-center gap-1.5" data-testid={`one-report-view-full-${r.slug}`}>
          View Full Report <ExternalLink size={12} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}

function Stars({ value }) {
  const n = value >= 90 ? 5 : value >= 80 ? 4 : value >= 70 ? 3 : value >= 60 ? 2 : 1;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={13} strokeWidth={2} className={i <= n ? "fill-[var(--ind)] text-[var(--ind)]" : "text-[var(--s300)]"} />
      ))}
    </div>
  );
}

function Metric({ k, v }) {
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className="text-body-sm font-medium text-[var(--s900)] mt-0.5">{v}</div>
    </div>
  );
}

// ─── Compare by What Parents Care About ──────────────────────────────────────

function ParentCareSections({ reports }) {
  const cards = [
    {
      title: "A. Can my child get in?",
      icon: Users,
      gradient: "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 60%, #FFFFFF 100%)",
      border: "border-indigo-100",
      rows: [
        { label: "Acceptance", get: (r) => r.uni.acceptance_rate_pct != null ? `${r.uni.acceptance_rate_pct}%` : null },
        { label: "IB Range",   get: (r) => r.uni.ib_score_range_low && r.uni.ib_score_range_high ? `${r.uni.ib_score_range_low}–${r.uni.ib_score_range_high} / 45` : null },
        { label: "SAT Range",  get: (r) => r.uni.sat_score_range_low && r.uni.sat_score_range_high ? `${r.uni.sat_score_range_low}–${r.uni.sat_score_range_high}` : null },
        { label: "IELTS",      get: (r) => r.uni.ielts_score_range_low && r.uni.ielts_score_range_high ? `${r.uni.ielts_score_range_low}–${r.uni.ielts_score_range_high}` : null },
      ],
    },
    {
      title: "B. Can we afford it?",
      icon: Wallet,
      gradient: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 50%, #FFFFFF 100%)",
      border: "border-amber-100",
      rows: [
        { label: "Total / yr",   get: (r) => fmtINR((r.uni.tuition_per_year_inr || 0) + (r.uni.living_per_year_inr || 0)) },
        { label: "Tuition / yr", get: (r) => r.uni.tuition_per_year_inr ? fmtINR(r.uni.tuition_per_year_inr) : null },
        { label: "Living / yr",  get: (r) => r.uni.living_per_year_inr ? fmtINR(r.uni.living_per_year_inr) : null },
        { label: "Scholarships", get: (r) => r.uni.scholarships_available ? "Available" : null },
      ],
    },
    {
      title: "C. Will this lead to a good career?",
      icon: BarChart3,
      gradient: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 50%, #FFFFFF 100%)",
      border: "border-emerald-100",
      rows: [
        { label: "Employment",  get: (r) => r.uni.employment_rate_pct != null ? `${r.uni.employment_rate_pct}%` : null },
        { label: "QS Rank",     get: (r) => r.uni.qs_rank_2025 != null ? `#${r.uni.qs_rank_2025}` : null },
        { label: "Career ROI",  get: (r) => r.plan?.university?.median_graduate_salary_inr && r.plan?.grand_total ? `${(r.plan.university.median_graduate_salary_inr * 5 / r.plan.grand_total).toFixed(1)}x` : null },
      ],
    },
    {
      title: "D. Can my child stay after graduation?",
      icon: Plane,
      gradient: "linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 50%, #FFFFFF 100%)",
      border: "border-rose-100",
      rows: [
        { label: "Post-study work", get: (r) => r.uni.post_study_work_years != null ? `${r.uni.post_study_work_years} ${r.uni.post_study_work_years === 1 ? "year" : "years"}` : null },
        { label: "Visa readiness",  get: (r) => r.visaReadiness },
      ],
    },
  ];

  return (
    <section className="mb-12" data-testid="one-report-care-sections">
      <h2 className="text-h2 font-semibold text-[var(--s900)] mb-5">What do parents think about</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {cards.map((c) => (
          <CareCard key={c.title} title={c.title} Icon={c.icon} rows={c.rows} reports={reports} gradient={c.gradient} border={c.border} />
        ))}
      </div>
    </section>
  );
}

function CareCard({ title, Icon, rows, reports, gradient, border }) {
  return (
    <div
      className={`card p-6 border ${border || ""}`}
      style={gradient ? { background: gradient } : undefined}
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-white text-[var(--ind)] flex items-center justify-center shadow-sm">
          <Icon size={16} strokeWidth={2} />
        </div>
        <h3 className="text-h3 font-semibold text-[var(--s900)]">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-body-sm">
          <thead>
            <tr className="text-left">
              <th className="py-2 pr-3 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]"></th>
              {reports.map((r) => (
                <th key={r.slug} className={`py-2 pr-3 text-label font-semibold ${BAND_TOKENS[r.band].chipText}`}>
                  {r.short.replace(/ University.*/, "")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-[var(--s100)]">
                <td className="py-2.5 pr-3 text-[var(--s600)]">{row.label}</td>
                {reports.map((r) => {
                  const v = row.get(r);
                  return (
                    <td key={r.slug} className="py-2.5 pr-3 font-medium text-[var(--s900)]">
                      {v != null && v !== "—" ? v : <SkeletonBar />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Detailed Comparison ─────────────────────────────────────────────────────

function DetailedComparison({ reports }) {
  const rows = [
    { k: "Country",            get: (r) => r.country },
    { k: "QS Rank 2025",       get: (r) => r.uni.qs_rank_2025 != null ? `#${r.uni.qs_rank_2025}` : null },
    { k: "IB Range",           get: (r) => r.uni.ib_score_range_low && r.uni.ib_score_range_high ? `${r.uni.ib_score_range_low}–${r.uni.ib_score_range_high} / 45` : null },
    { k: "SAT Range",          get: (r) => r.uni.sat_score_range_low && r.uni.sat_score_range_high ? `${r.uni.sat_score_range_low}–${r.uni.sat_score_range_high}` : null },
    { k: "IELTS Range",        get: (r) => r.uni.ielts_score_range_low && r.uni.ielts_score_range_high ? `${r.uni.ielts_score_range_low}–${r.uni.ielts_score_range_high}` : null },
    { k: "Acceptance",         get: (r) => r.uni.acceptance_rate_pct != null ? `${r.uni.acceptance_rate_pct}%` : null },
    { k: "Tuition / yr",       get: (r) => r.uni.tuition_per_year_inr ? fmtINR(r.uni.tuition_per_year_inr) : null },
    { k: "Living / yr",        get: (r) => r.uni.living_per_year_inr ? fmtINR(r.uni.living_per_year_inr) : null },
    { k: "Total / yr",         get: (r) => fmtINR((r.uni.tuition_per_year_inr || 0) + (r.uni.living_per_year_inr || 0)) },
    { k: "Employment",         get: (r) => r.uni.employment_rate_pct != null ? `${r.uni.employment_rate_pct}%` : null },
    { k: "Post-study work",    get: (r) => r.uni.post_study_work_years != null ? `${r.uni.post_study_work_years} ${r.uni.post_study_work_years === 1 ? "year" : "years"}` : null },
    { k: "Visa readiness",     get: (r) => r.visaReadiness },
    { k: "Scholarships",       get: (r) => r.uni.scholarships_available ? "Available" : null },
  ];

  return (
    <section className="mb-12" data-testid="one-report-detailed">
      <h2 className="text-h2 font-semibold text-[var(--s900)] mb-5">Detailed Comparison</h2>
      <div className="card p-2 md:p-3 overflow-x-auto">
        <table className="min-w-full text-body-sm">
          <thead>
            <tr>
              <th className="text-left py-3 px-4 bg-[var(--ind-soft)] text-[var(--ind)] text-label font-semibold uppercase tracking-[0.05em] rounded-l-[var(--r12)]">Metric</th>
              {reports.map((r, idx) => {
                const t = BAND_TOKENS[r.band];
                const last = idx === reports.length - 1;
                return (
                  <th key={r.slug} className={`text-left py-3 px-4 ${t.colTint} ${t.colHeader} font-semibold text-body-sm ${last ? "rounded-r-[var(--r12)]" : ""}`}>
                    {r.name}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.k} className="border-t border-[var(--s100)]">
                <td className="py-2.5 px-4 text-[var(--s700)] font-medium whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">{row.k} <Info size={11} className="text-[var(--s300)]" /></span>
                </td>
                {reports.map((r) => {
                  const v = row.get(r);
                  return (
                    <td key={r.slug} className={`py-2.5 px-4 ${BAND_TOKENS[r.band].colTint}`}>
                      {v != null && v !== "—" ? <span className="text-[var(--s900)] font-medium">{v}</span> : <SkeletonBar />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Trade-Offs ──────────────────────────────────────────────────────────────

function TradeOffsRow({ reports }) {
  return (
    <section className="mb-12" data-testid="one-report-tradeoffs">
      <h2 className="text-h2 font-semibold text-[var(--s900)] mb-5">Key Trade-Offs Parents Should Know</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {reports.map((r) => <TradeOffCard key={r.slug} r={r} />)}
      </div>
    </section>
  );
}

function TradeOffCard({ r }) {
  const t = BAND_TOKENS[r.band];
  const items = Array.isArray(r.uni.tradeoffs) ? r.uni.tradeoffs : [];
  return (
    <div className={`card p-5 ${t.colTint} border ${t.chipBorder}`}>
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className={t.chipText} strokeWidth={2} />
        <div className={`text-label font-semibold ${t.chipText}`}>{r.name}</div>
      </div>
      {items.length > 0 ? (
        <ul className="space-y-2 text-body-sm text-[var(--s700)]">
          {items.map((tx, i) => (
            <li key={i} className="flex items-start gap-2">
              <Info size={13} className={`${t.chipText} mt-0.5 shrink-0`} strokeWidth={2} />
              <span>{tx}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2">
          <SkeletonBar w="w-2/3" />
          <SkeletonBar w="w-1/2" />
        </div>
      )}
    </div>
  );
}

// ─── Bottom Parent Note ──────────────────────────────────────────────────────

function ParentNote() {
  return (
    <section className="rounded-[var(--r20)] bg-[var(--ind-soft)] border border-indigo-100 p-5 md:p-6 flex items-start gap-3" data-testid="one-report-parent-note">
      <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-indigo-200 text-[var(--ind)] flex items-center justify-center">
        <Trophy size={16} strokeWidth={2} />
      </div>
      <p className="text-body-sm text-[var(--s700)] leading-relaxed">
        <span className="font-semibold text-[var(--s900)]">Parent Note:</span> These verdicts are based on verified data and your profile match. Review each university in detail and consider what matters most for your child&apos;s future.
      </p>
    </section>
  );
}

// ─── Shared placeholders ─────────────────────────────────────────────────────

function SkeletonBar({ w = "w-16" }) {
  return <div className={`inline-block h-3 ${w} rounded bg-[var(--s100)] animate-pulse align-middle`} />;
}

function SkeletonCard() {
  return (
    <div className="rounded-[var(--r16)] bg-white border border-[var(--s200)] p-4 space-y-3">
      <SkeletonBar w="w-24" />
      <SkeletonBar w="w-40" />
      <SkeletonBar w="w-full" />
    </div>
  );
}

function SkeletonBlock({ testid }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      <SkeletonBar w="w-2/3" />
      <SkeletonBar w="w-1/2" />
      <SkeletonBar w="w-3/4" />
    </div>
  );
}
